import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authConfig } from '../config/auth';
import { getAccessToken, getUserInfo } from '../utils/myLoginAuth';
import { loadUserFromStorage } from '../services/api/user/userApi';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../actions/authActions';

// Create context
const AuthContext = createContext();

// Initial state
const initialState = {
  isAuthenticated: false,
  user: null,
  authProvider: null,
  loading: true,
  error: null,
};

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS', 
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING'
};

// Reducer
function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        loading: true,
        error: null
      };
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        authProvider: action.payload.provider,
        loading: false,
        error: null
      };
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        authProvider: null,
        loading: false,
        error: action.payload
      };
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        authProvider: null,
        loading: false
      };
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    default:
      return state;
  }
}

// Provider component
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const reduxDispatch = useDispatch();

  // Check for existing authenticated sessions on mount
  useEffect(() => {
    async function checkAuthStatus() {
      try {
        const userFromStorage = await loadUserFromStorage();
        
        if (userFromStorage) {
          dispatch({ 
            type: AUTH_ACTIONS.LOGIN_SUCCESS, 
            payload: { 
              user: userFromStorage, 
              provider: userFromStorage.provider || 'unknown' 
            } 
          });

          reduxDispatch(loginSuccess({
            user: userFromStorage,
            provider: userFromStorage.provider || 'unknown',
            isAuthenticated: true
          }));
          
          return;
        }
        
        const token = await getAccessToken();
        if (token) {
          const userInfo = getUserInfo();
          if (userInfo) {
            dispatch({ 
              type: AUTH_ACTIONS.LOGIN_SUCCESS, 
              payload: { 
                user: userInfo, 
                provider: 'mylogin' 
              } 
            });
            
            reduxDispatch(loginSuccess({
              user: userInfo,
              provider: 'mylogin',
              isAuthenticated: true
            }));
            
            return;
          }
        }
        
        // No active authentication found
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      } catch (error) {
        console.error("Error checking authentication status:", error);
        dispatch({ 
          type: AUTH_ACTIONS.LOGIN_FAILURE, 
          payload: "Failed to verify authentication status" 
        });
      }
    }

    checkAuthStatus();
  }, [reduxDispatch]);

  // Make the context value
  const value = {
    ...state,
    dispatch,
    actions: AUTH_ACTIONS
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
